<!--
  Created 10.10.2023
  Media Gallery component for the proposal show page.
-->
<template>
  <ProposalSection
    section="mediaGallery"
    :content-editable="contentEditable"
    :cta="(galleryImages.length > 2 ? {
      text: 'Se flere',
      icon: ArrowRightIcon,
    }:{})"
    :cta-footer="(contentEditable ? {
      text: 'Endre bilder i galleriet',
      icon: SquaresPlusIcon,
      class: '!text-gray-600'
    }:{})"
    @heading-cta-clicked="mediaCarousel.nextSlide()"
    @footer-cta-clicked="showModal=true">
    <template #content>
      <Media
        v-if="galleryImages.length > 0"
        ref="mediaCarousel"
        :media-items="galleryImages"
        :image-classes="{'w-1/2 h-auto': galleryImages.length < 2}"
        :carousel-break-points="{
          default: 1,
          sm: 1.15,
          md: 1.2,
          lg: 1.25,
          xl: 1.25,
          '2xl': 2.2,
        }"
        is-rounded
        has-large-height-mobile
        :is-carousel="!store.printMode"
        grid-column-class="grid-cols-2 !gap-4"
        carousel-bleed="max-sm:full sm:right" />
      <div v-else>
        <div
          v-if="contentEditable"
          class="flex justify-center space-x-8">
          <img
            class="aspect-[16/10] h-auto w-1/2 bg-gray-300 object-cover pl-4 pt-4 text-sm italic text-gray-400"
            src=""
            alt="Image not selected">
          <img
            class="aspect-[16/10] h-auto w-1/2 bg-gray-300 object-cover pl-4 pt-4 text-sm italic text-gray-400"
            src=""
            alt="Image not selected">
        </div>
        <p
          v-else
          class="text-lg italic text-gray-500">
          Det er ikke lagt inn noe flere bilder her.
        </p>
      </div>
    </template>
  </ProposalSection>
  <SimpleModal
    v-if="contentEditable && proposal"
    box-class="w-11/12 md:w-4/5 lg:w-10/12 max-w-[1000px] max-h-[80vh] overflow-y-auto"
    :show="showModal"
    @update:show="(value) => showModal = value">
    <template #content>
      <div class="space-y-4 p-8">
        <h2 class="text-2xl font-medium">
          Bildegalleri for {{ proposalDestination.name }}
        </h2>
        <p>
          Her kan du legge til og fjerne bilder fra galleriet.
        </p>
        <div class="h-24 w-full">
          <media-picker-multiple
            :selected="galleryImages"
            :settings="{
              proposal: {
                name: 'Current Proposal',
                get: route('api.proposal.media.index', {proposal: proposal.id}),
                upload: route('api.proposal.media.store', {proposal: proposal.id})
              },
              destination: {
                name: proposal.destinations[0]?.name ?? 'Original destination',
                get: route('api.destination.entity.media.index', {entity: proposal.destinations[0]?.id}),
                upload: route('api.destination.entity.media.store', {entity: proposal.destinations[0]?.id})
              }
            }"
            :default_search="proposal.destinations[0] ? proposal.destinations[0]?.name + ', ' + proposal.destinations[0]?.parent?.name : ''"
            @update="updateGalleryMedia" />
        </div>
        <p class="text-sm font-medium">
          Bilder i galleriet
        </p>
        <Media
          :media-figure-class="{'!w-1/5 !h-auto': galleryImages.length < 2}"
          :media-items="galleryImages"
          hide-fig-caption
          grid-column-class="grid-cols-5 !gap-4" />
      </div>
    </template>
  </SimpleModal>
</template>
<script setup>
/* external */
import {
  ArrowRightIcon, SquaresPlusIcon,
} from '@heroicons/vue/24/outline';
import {
  computed, onMounted, ref,
} from 'vue';
/* local */
import ProposalSection from '@/Pages/Proposal/Components/ProposalSection.vue';
import Media from '@/Pages/Proposal/Components/Media.vue';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import MediaPickerMultiple from '@/Components/Media/MediaPickerMultiple.vue';
import { useProposalStore } from '@/Stores/Proposal';
import MediaSelectSingle from '@/Components/Media/MediaPickerSingle.vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
});

const mediaCarousel = ref(null);
const showModal = ref(false);

const store = useProposalStore();
const proposal = computed(() => store.proposal);
const proposalDestination = computed(() => proposal.value.data.destination);

const galleryImages = computed(() => proposal.value.gallery);
const loadingGalleryImages = ref(false);

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * Update gallery images
 * @param {Array} media - array of media objects
 */
function updateGalleryMedia(media) {
  loadingGalleryImages.value = true;

  axios.post(route('api.proposal.gallery.sync', { proposal: proposal.value.id }), {
    images: media,
  }).then((response) => {
    proposal.value.gallery = response.data.images;
  }).catch((error) => {
    console.error('error with image gallery update: ', error);
  }).finally(() => {
    loadingGalleryImages.value = false;
  });
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

</script>
